import clsx from 'clsx';
import { has, isArray, isPlainObject, some } from 'lodash';
import { arrayOf, bool, func, node, number, oneOf, oneOfType, shape, string } from 'prop-types';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactSelect, { components } from 'react-select';

import { FieldErrorMessage, Icon } from 'components';

import 'assets/styles/components/form/form-select.scss';

const MenuPortal = ({ children, ...props }) => (
  <components.MenuPortal className="select__menu-portal" {...props}>
    <div data-test-id="selectMenuPortal">{children}</div>
  </components.MenuPortal>
);

const FormSelect = ({
  options,
  name,
  onChange,
  id = '',
  placeholder = '',
  disabled = false,
  isMulti = false,
  className = '',
  icon = null,
  noOptionKey = 'form:select.empty',
  label = '',
  isLoading = false,
  prefillFromParams = false,
  isSearchable = true
}) => {
  const { t } = useTranslation();
  const {
    getValues,
    formState: { errors }
  } = useFormContext();
  const error = errors?.[name]?.message || errors?.[name]?.type;

  useEffect(() => {
    if (prefillFromParams) {
      /*
      If the select is pre-filled from the URL params, we need to set the value and the label
      Check if option has already been formatted to avoid formatting it twice
      */
      const prefillValue = options.find((option) => {
        return option.id?.toLowerCase() === getValues(name)?.value?.toLowerCase();
      });
      if (prefillValue && !isOptionValid(getValues(name))) onChange(prefillValue);
    }
  }, [prefillFromParams, options, getValues(name)]);

  // currentValue must contain the keys `value` and `label` to be compatible with react-select
  const isOptionValid = (option) => has(option, 'value') && has(option, 'label');

  const onSelectOption = (option) => {
    let formattedOption = option;

    if (isArray(formattedOption) && some(formattedOption, (opt) => !isOptionValid(opt))) {
      formattedOption = formattedOption.map((opt) => formatOption(opt));
    } else if (isPlainObject(formattedOption) && !isOptionValid(formattedOption)) {
      formattedOption = formatOption(formattedOption);
    }

    onChange(formattedOption);
  };

  const formatOption = (option) => {
    return { ...option, label: option?.props?.children || option?.label, value: option?.value || option?.id };
  };

  return (
    <div className={clsx('relative form-select-container', className)}>
      {icon?.name && (
        <div className="select-icon">
          <Icon name={icon.name} iconStyle={icon?.style} classes={icon?.class} />
        </div>
      )}

      {label && <div className="select-label font-bold text-gray-700 uppercase">{label}</div>}

      <ReactSelect
        name={name}
        isMulti={isMulti}
        components={{
          IndicatorSeparator: () => null,
          DropdownIndicator: () => (
            <div className="select__arrow">
              <Icon name="angle-down" />
            </div>
          ),
          MenuPortal: (props) => <MenuPortal {...props} />
        }}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        menuPortalTarget={document.getElementById('react-select-portal')}
        options={options}
        placeholder={placeholder}
        noOptionsMessage={() => t(noOptionKey)}
        onChange={onSelectOption}
        isDisabled={disabled}
        value={getValues(name)}
        classNamePrefix="select"
        className={clsx('form-select', icon?.name && 'has-icon', error && 'invalid')}
        closeMenuOnSelect={!isMulti}
        maxMenuHeight={300}
        id={id}
        isLoading={isLoading}
        loadingMessage={() => t('loading')}
        isSearchable={isSearchable}
      />

      <FieldErrorMessage errors={errors} name={name} />
    </div>
  );
};

MenuPortal.propTypes = {
  children: node,
  size: string
};

FormSelect.propTypes = {
  options: arrayOf(
    shape({
      value: oneOfType([string, number]),
      label: oneOfType([string, node]),
      uuid: string
    })
  ).isRequired,
  name: string.isRequired,
  onChange: func.isRequired,
  id: string,
  placeholder: string,
  disabled: bool,
  isMulti: bool,
  className: string,
  icon: shape({ name: string.isRequired, style: oneOf(['fas', 'fad']), class: string }),
  noOptionKey: string,
  label: oneOfType([string, node]),
  isLoading: bool,
  // If true, the select will be prefilled with the value from searchParams
  prefillFromParams: bool,
  isSearchable: bool
};

export default FormSelect;
